import { Menu } from 'lucide-react';
import { logout, performLogout, updateUserLanguage } from '../../redux/authSlice';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import India from '../../assets/img/India.png';
import France from '../../assets/img/France.png';
import Swal from 'sweetalert2';
import { Heart, LogOut, Languages } from 'lucide-react';
import logo from '../../assets/img/logo-TT2.png';
import { getLanguages } from '../../redux/translationSlice';
import { docPatCreationCountryList } from '../../redux/masterSlice';
import ct from 'countries-and-timezones';
import * as Flags from 'country-flag-icons/react/3x2';

export default function Header({ onMenuClick }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation('header');
  const lang = localStorage.getItem('i18nextLng') || 'en';
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1270);
  const [isLanguagePopupOpen, setIsLanguagePopupOpen] = useState(false);
  const languagePopupRef = useRef(null);
  const isAuthenticated = localStorage.getItem('isAuthenticated');
  // Pre-built axios instance for the language-update API call. Hooks must be called
  // at component top level, not inside event handlers.
  const axiosInstance = useAxiosAuth();

  // Dynamic language list — pulled from the language master so adding a language in
  // the admin panel instantly shows up here without a code change.
  const languages = useSelector((state) => state.translation?.languages || []);
  const selectedDoctor = useSelector((state) => state.doctor?.selectedDoctor);
  const countryTimezoneList = useSelector((state) => state.master?.countryTimezoneList || []);
  const userRole = localStorage.getItem('Role');
  useEffect(() => {
    if (!languages || languages.length === 0) {
      dispatch(getLanguages({ page: 1, limit: 100, search: '' }));
    }
    // Intentionally only run once on mount. AppContent also loads the list on boot;
    // this is a safety net for direct renders of Header without going through App.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userRole === 'doctor' && (!countryTimezoneList || countryTimezoneList.length === 0)) {
      dispatch(docPatCreationCountryList());
    }
  }, [userRole, countryTimezoneList, dispatch]);

  // Normalize timezone aliases (browsers differ: "Asia/Calcutta" vs "Asia/Kolkata")
  const normalizeTz = (tz) => {
    if (!tz) return '';
    if (tz === 'Asia/Calcutta') return 'Asia/Kolkata';
    return tz;
  };

  const browserTimeZone = normalizeTz(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Resolve doctor's country name — try populated country object, then fallback to countryId lookup
  const doctorCountryId =
    (typeof selectedDoctor?.country === 'object' ? selectedDoctor?.country?.countryId : null) ||
    (typeof selectedDoctor?.country === 'string' ? selectedDoctor?.country : null) ||
    selectedDoctor?.countryId ||
    '';
  const doctorMasterEntry = countryTimezoneList?.find((c) => c.countryId === doctorCountryId);
  const doctorCountryName =
    selectedDoctor?.country?.countryName || doctorMasterEntry?.countryName || '';
  // Master's countryCode is a phone dial code (e.g. "+33"), not an ISO 3166-1 alpha-2 code.
  // Resolve the ISO code by matching the country name against countries-and-timezones'
  // built-in country list, so any country known to the library gets a flag.
  const nameToIsoCode = (name) => {
    if (!name) return '';
    const target = name.trim().toLowerCase();
    const all = ct.getAllCountries();
    const match = Object.values(all).find((c) => c.name.toLowerCase() === target);
    return match?.id || '';
  };
  const doctorCountryCode = nameToIsoCode(doctorCountryName).toUpperCase();

  // Resolve the current (browser) timezone's country using countries-and-timezones,
  // so it works for any country even if it's missing from the master list.
  const browserTzCountries = ct.getCountriesForTimezone(browserTimeZone) || [];
  const browserTzCountryName = browserTzCountries?.[0]?.name || '';
  const browserTzCountryCode = (browserTzCountries?.[0]?.id || '').toUpperCase();

  const DoctorFlag = doctorCountryCode ? Flags[doctorCountryCode] : null;
  const BrowserFlag = browserTzCountryCode ? Flags[browserTzCountryCode] : null;

  const showTzMismatch =
    userRole === 'doctor' &&
    doctorCountryName &&
    browserTzCountryName &&
    doctorCountryName !== browserTzCountryName;

  // State for timezone and flag
  const [timezoneInfo, setTimezoneInfo] = useState({ name: '', flag: '🌍' });

  const getFlag = (country) => {
    if (!country) return '🌍';
    const lower = country.toLowerCase();
    if (lower.includes('india')) return <img src={India} alt={t('header.india_flag')} className="w-8 h-8" />;
    if (lower.includes('france')) return <img src={France} alt={t('header.france_flag')} className="w-8 h-8" />;
    return '🌍';
  };

  const getCountryFromTimezone = (timezone) => {
    if (!timezone) return '';
    console.log('System Timezone:', timezone);
    if (timezone === 'Asia/Kolkata' || timezone.includes('Asia/Calcutta')) return 'India';
    if (timezone === 'Europe/Paris') return 'France';
    return '';
  };

  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = getCountryFromTimezone(timezone);
    const flag = getFlag(country);
    console.log('Mapped Country:', country);
    setTimezoneInfo({ name: timezone, flag });
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: t('header.logoutConfirmation'),
      text: t('header.logoutMessage'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4ABDBD',
      cancelButtonColor: '#d33',
      confirmButtonText: t('header.logout'),
      cancelButtonText: t('header.cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Await the thunk so localStorage is fully cleared before we navigate.
        // Otherwise Login's auto-redirect effect reads stale auth state and
        // bounces us back to the dashboard, and the Sidebar's `|| 'patient'`
        // fallback paints the patient menu while role is briefly null.
        await dispatch(performLogout({ axiosInstance, lang }));
        navigate('/login', { replace: true });
      }
    });
  };

  const handleLanguageChange = (language) => {
    // Switch i18n locally so the UI updates immediately.
    i18n.changeLanguage(language);
    setIsLanguagePopupOpen(false);

    // Persist the choice to the user record so the next login honours it.
    // Only fire if the user is authenticated — the update endpoint requires a token.
    if (localStorage.getItem('isAuthenticated') === 'true' && localStorage.getItem('accessToken')) {
      dispatch(updateUserLanguage({ lang: language, axiosInstance }));
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1270);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languagePopupRef.current && !languagePopupRef.current.contains(event.target)) {
        setIsLanguagePopupOpen(false);
      }
    };
    if (isLanguagePopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguagePopupOpen]);

  return (
    <header
      data-testid="main-header"
      className="flex items-center justify-between p-4 h-auto sm:h-[65.5px] bg-white shadow sticky top-0 z-20 w-full"
    >
      {/* Left Section - Menu Toggle */}
      <div className="flex items-center gap-3">
        <button
          data-testid="menu-toggle-button"
          onClick={onMenuClick}
          className="p-1.5 text-gray-600 focus:outline-none cursor-pointer"
          aria-label={t('header.toggle_sidebar')}
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* Center Section - Mobile Title */}
      {window.innerWidth < 1024 && (
        <div data-testid="mobile-header-title" className="flex items-center text-white font-bold text-xl">
          <div className="mr-1.5">
            <img src={logo} className="h-10 w-10 text-white" />
          </div>
          <span data-testid="header-title" className="text-xl font-bold bg-gradient-to-r from-[#3ca7a7] to-[#3AA8A8] bg-clip-text text-transparent">{t('header.heading')}</span>
        </div>
      )}

      {/* Right Section - Actions */}
      <div data-testid="header-actions" className="relative flex items-center gap-3">
        {showTzMismatch && (
          <div className="relative group">
            <div
              data-testid="tz-mismatch-indicator"
              className="flex items-center gap-1 px-3 py-1 rounded-lg bg-white border border-gray-200 shadow-sm text-sm font-medium text-gray-700 whitespace-nowrap cursor-help"
            >
              {DoctorFlag && (
                <DoctorFlag title={doctorCountryName} className="w-5 h-auto rounded-sm" />
              )}
              <span>{doctorCountryName}</span>
              <span className="mx-1">&rarr;</span>
              {BrowserFlag && (
                <BrowserFlag title={browserTzCountryName} className="w-5 h-auto rounded-sm" />
              )}
              <span>{browserTzCountryName}</span>
            </div>
            <div
              data-testid="tz-mismatch-tooltip"
              role="tooltip"
              className="pointer-events-none absolute top-full right-0 mt-2 w-72 px-3 py-2 rounded-md bg-gray-900 text-white text-xs leading-snug shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50"
            >
              {t('header.tz_mismatch_tooltip', { doctorCountry: doctorCountryName, browserCountry: browserTzCountryName })}
            </div>
          </div>
        )}
        {/* Language Selector */}
        <div className="relative">
          <button
            data-testid="language-toggle-button"
            onClick={() => setIsLanguagePopupOpen(!isLanguagePopupOpen)}
            className='bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] backdrop-blur-sm flex items-center justify-center text-white shadow rounded-2xl p-2 border border-white/30 cursor-pointer'
            aria-label={t('header.select_language')}
          >
            <Languages className="h-6 w-6" />
            {/* <span className="text-lg">{i18n.language === 'fr' ? 'Fr' : 'En'}</span> */}
          </button>
          {isLanguagePopupOpen && (
            <div
              data-testid="language-popup"
              ref={languagePopupRef}
              className="absolute right-0 top-full mt-2 w-44 max-h-72 overflow-y-auto bg-white rounded-lg shadow-lg p-2 z-40 border border-gray-200"
            >
              {languages && languages.length > 0 ? (
                languages.map((langItem) => {
                  const code = (langItem.code || '').toLowerCase();
                  const active = i18n.language === code;
                  return (
                    <button
                      key={langItem.languageId || code}
                      data-testid={`language-option-${code}`}
                      onClick={() => handleLanguageChange(code)}
                      className={`w-full text-left mb-1 last:mb-0 px-4 cursor-pointer py-2 text-sm rounded-md ${active
                        ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate">{langItem.name}</span>
                        <span className={`text-[10px] font-bold uppercase ${active ? 'text-white/80' : 'text-gray-400'}`}>{code}</span>
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-2 text-xs text-gray-400">
                  {t('header.no_languages', 'No languages')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Logout Button */}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="p-1.5 text-gray-600 focus:outline-none cursor-pointer"
          aria-label={t('header.logout')}
        >
          <LogOut className="h-6 w-6" />
        </button>
      </div>
    </header>
  );
}